import { observerElement } from 'components/StoreLayout/useObserver';
import { mapBundleViewModel } from 'components/StoreLayout/utils';
import { motion } from 'framer-motion';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStoreState } from 'state/hooks/store.state.hook';
import {
  EBundlesInternalViewModel,
  ESaleDiscountDisplayType,
} from 'utils/enums';
import { IProductResponse, OfferData, ProductData } from 'utils/types';

export interface BundlesProps {
  data: OfferData;
  selectOffer: () => void;
  index: number;
  threshold?: number;
  shouldFocus: boolean;
  tooltipOpenEventHandler?: () => void;
  tooltipLoadCallback?: (arr: string[]) => Promise<IProductResponse[]>;
}

const APPEARANCE_DELAY = 0.15;

export const Bundle: React.FC<BundlesProps> = ({
  data,
  selectOffer,
  index,
  threshold,
  shouldFocus,
  tooltipOpenEventHandler,
  tooltipLoadCallback,
}) => {
  const { storeTheme, translations, sendEvent } = useStoreState();

  const [isDoneAnimating, setIsDoneAnimating] = useState<boolean>(false);
  const [animateCompleted, setAnimateCompleted] = useState(false);
  const bundleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!bundleRef?.current || !sendEvent) return;
    observerElement(bundleRef.current, data, sendEvent);
  }, [bundleRef?.current, sendEvent]);

  const BundleDesign =
    mapBundleViewModel[
      storeTheme?.general?.bundlesInternalViewModel ||
        EBundlesInternalViewModel.AK
    ];

  const animationDelay = useMemo(() => {
    if (!threshold) return index * APPEARANCE_DELAY;
    if (index < threshold) return index * APPEARANCE_DELAY;
    return (index - threshold) * APPEARANCE_DELAY;
  }, [threshold, index]);

  // Trigger re-rendering when screen size changing
  const [, setScreenWidth] = useState(window.innerWidth);
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setScreenWidth(window.innerWidth);
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (shouldFocus && isDoneAnimating) {
      bundleRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [shouldFocus, isDoneAnimating]);

  const mapPropsToBundle: Partial<Record<EBundlesInternalViewModel, any>> = {
    [EBundlesInternalViewModel.LIGHTNING_LINK_CASINO]: {
      tooltipLoadCallback: tooltipLoadCallback,
    },
    [EBundlesInternalViewModel.MIGHTY_FU_CASINO]: {
      tooltipLoadCallback: tooltipLoadCallback,
    },
    [EBundlesInternalViewModel.HEART_OF_VEGAS]: {
      tooltipLoadCallback: tooltipLoadCallback,
    },
    [EBundlesInternalViewModel.CASHMAN_CASINO]: {
      tooltipLoadCallback: tooltipLoadCallback,
    },
    [EBundlesInternalViewModel.BIG_FISH_CASINO]: {
      gameName: EBundlesInternalViewModel.BIG_FISH_CASINO,
    },
    [EBundlesInternalViewModel.JACKPOT_SLOTS]: {
      gameName: EBundlesInternalViewModel.JACKPOT_SLOTS,
      translations: { includes: translations!.subProductsPrefixText },
    },
  };

  const bundleProps = storeTheme?.general?.bundlesInternalViewModel
    ? mapPropsToBundle[storeTheme?.general?.bundlesInternalViewModel]
    : {};

  return (
    <motion.div
      ref={bundleRef}
      className="bundle-top-container"
      key={data.offerId}
      data-testid={`${data.offerId}`}
      initial={{ y: 100, opacity: 0 }}
      animate={{
        y: 0,
        opacity: 1,
      }}
      transition={{
        opacity: {
          duration: 0.25,
          ease: 'easeIn',
          delay: animationDelay,
        },
        y: {
          duration: 0.35,
          ease: 'easeOut',
          delay: animationDelay,
        },
      }}
      onAnimationComplete={() => setIsDoneAnimating(true)}
    >
      <motion.div
        style={{ cursor: 'pointer', height: '100%' }}
        whileHover={{
          scale:
            storeTheme?.general?.bundlesInternalViewModel ===
            EBundlesInternalViewModel.MAIN_SUB
              ? 1
              : 1.05,
          zIndex: 100,
        }}
        transition={{
          scale: {
            duration: 0.25,
          },
        }}
        onClick={selectOffer}
        onAnimationComplete={() => setAnimateCompleted(true)}
      >
        <BundleDesign
          loaded={animateCompleted}
          productList={data.productsSequence[0].products.map(
            (p: ProductData) => p,
          )}
          width="100%"
          bundleBorderColor={{
            gradientDirection:
              storeTheme?.storeScreen.bundleBorderColor.gradientDirection,
            colorOne: storeTheme?.storeScreen.bundleBorderColor.colorOne,
            colorTwo: storeTheme?.storeScreen.bundleBorderColor.colorTwo,
          }}
          bundleButtonColor={storeTheme?.general.buttonColor}
          bundleButtonTextColor={storeTheme?.general.buttonTextColor}
          backgroundSrc={data.offerUi.backgroundImage}
          prices={data.productsSequence[0].prices}
          fontFamily={storeTheme?.general.font}
          badges={data?.badges} // TODO remove old badge schema
          emblemBadges={data?.offerBadges?.emblems}
          ribbonBadges={data?.offerBadges?.ribbons}
          backgroundColor={data.offerUi.specialOffer?.backgroundColor}
          salePercentage={data.productSale?.sale}
          salePercentageDisplayType={
            data.productSale?.type || ('percentage' as ESaleDiscountDisplayType)
          }
          priceDiscount={data.priceDiscount?.discount}
          priceDiscountDisplayType={
            data.priceDiscount?.type ||
            ('percentage' as ESaleDiscountDisplayType)
          }
          bundleRef={bundleRef}
          availability={data.playerAvailability}
          playerAvailability={data.offerPlayerRemainingAvailability}
          borderRadius={storeTheme?.storeScreen.borderRadius}
          tooltipOpenEventHandler={tooltipOpenEventHandler}
          {...bundleProps}
        />
      </motion.div>
    </motion.div>
  );
};
